import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Table, Alert, Spinner, Accordion, Card, Row, Col, Badge } from "react-bootstrap";
import { FaSearch, FaFilter, FaUsers } from "react-icons/fa";

function EditStudentsModal({
  show,
  onHide,
  schoolid,
  getStudentsBySchoolId,
  getClassesBySchoolId,
  updateStudent,
  teacherid,
  teacherName
}) {
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [editedStudents, setEditedStudents] = useState({}); // Track changes
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Filter states
  const [gradeFilter, setGradeFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [learningFlagsFilter, setLearningFlagsFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Available grade levels using boolean system
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  // Learning support flag options
  const learningFlagsOptions = [
    { value: "all", label: "All Students" },
    { value: "esl", label: "EAL Only" },
    { value: "sen", label: "Speech & Language Only" },
    { value: "asd", label: "ASD Only" },
    { value: "gdd", label: "GDD Only" },
    { value: "send", label: "SEND Only" },
    { value: "any_flags", label: "Any Learning Flags" },
    { value: "no_flags", label: "No Learning Flags" }
  ];

  // Load data when modal opens
  useEffect(() => {
    if (show && schoolid) {
      loadData();
    }
  }, [show, schoolid]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [classesResponse, studentsResponse] = await Promise.all([
        getClassesBySchoolId(schoolid),
        getStudentsBySchoolId(schoolid)
      ]);
      setClasses(classesResponse);
      setStudents(studentsResponse);
    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get grade level from class boolean fields
  const getGradeLevel = (cls) => {
    if (cls.grade_level1) return 'Grade 1';
    if (cls.grade_level2) return 'Grade 2';
    if (cls.grade_level3) return 'Grade 3';
    if (cls.grade_level4) return 'Grade 4';
    if (cls.grade_level5) return 'Grade 5';
    if (cls.grade_level6) return 'Grade 6';
    if (cls.grade_level7) return 'Grade 7';
    if (cls.grade_level8) return 'Grade 8';
    return '';
  };

  // Helper to get grade boolean from class boolean fields
  const getGradeBooleanFromClass = (cls) => {
    if (cls.grade_level1) return 'grade_level1';
    if (cls.grade_level2) return 'grade_level2';
    if (cls.grade_level3) return 'grade_level3';
    if (cls.grade_level4) return 'grade_level4';
    if (cls.grade_level5) return 'grade_level5';
    if (cls.grade_level6) return 'grade_level6';
    if (cls.grade_level7) return 'grade_level7';
    if (cls.grade_level8) return 'grade_level8';
    return 'unknown';
  };

  // Get available classes for filter dropdown
  const getAvailableClasses = () => {
    const classOptions = [{ value: "all", label: "All Classes" }];
    
    classes.forEach(cls => {
      classOptions.push({
        value: cls.id,
        label: `${cls.name} (${getGradeLevel(cls)})`
      });
    });

    // Add unassigned option if there are unassigned students
    const unassignedStudents = students.filter(student => 
      !student.classId || !classes.find(cls => cls.id === student.classId)
    );
    
    if (unassignedStudents.length > 0) {
      classOptions.push({
        value: "unassigned",
        label: "Unassigned Students"
      });
    }

    return classOptions;
  };

  // Filter students based on current filter settings
  const getFilteredStudentsByClass = () => {
    let filteredStudents = students;

    // Apply grade filter
    if (gradeFilter !== "all") {
      filteredStudents = filteredStudents.filter(student => {
        const cls = classes.find(c => c.id === student.classId);
        return cls ? getGradeBooleanFromClass(cls) === gradeFilter : false;
      });
    }

    // Apply class filter
    if (classFilter !== "all") {
      if (classFilter === "unassigned") {
        filteredStudents = filteredStudents.filter(student => 
          !student.classId || !classes.find(cls => cls.id === student.classId)
        );
      } else {
        filteredStudents = filteredStudents.filter(student => 
          student.classId === classFilter
        );
      }
    }

    // Apply learning flags filter
    if (learningFlagsFilter !== "all") {
      filteredStudents = filteredStudents.filter(student => {
        switch (learningFlagsFilter) {
          case "esl":
            return student.esl;
          case "sen":
            return student.sen;
          case "asd":
            return student.asd;
          case "gdd":
            return student.gdd;
          case "send":
            return student.send;
          case "any_flags":
            return student.esl || student.sen || student.asd || student.gdd || student.send;
          case "no_flags":
            return !student.esl && !student.sen && !student.asd && !student.gdd && !student.send;
          default:
            return true;
        }
      });
    }

    // Apply search term filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredStudents = filteredStudents.filter(student => {
        const cls = classes.find(c => c.id === student.classId);
        return (
          student.name?.toLowerCase().includes(searchLower) ||
          cls?.name?.toLowerCase().includes(searchLower)
        );
      });
    }

    return filteredStudents;
  };

  // Group students by class (using filtered data)
  const studentsByClass = React.useMemo(() => {
    const grouped = {};
    const filteredStudents = getFilteredStudentsByClass();
    
    classes.forEach(cls => {
      const studentsInClass = filteredStudents.filter(student => student.classId === cls.id);
      if (studentsInClass.length > 0) {
        grouped[cls.id] = {
          className: cls.name,
          gradeLevel: getGradeLevel(cls),
          students: studentsInClass
        };
      }
    });

    // Add unassigned students if they exist in filtered results
    const unassignedStudents = filteredStudents.filter(student => 
      !student.classId || !classes.find(cls => cls.id === student.classId)
    );
    
    if (unassignedStudents.length > 0) {
      grouped['unassigned'] = {
        className: 'Unassigned Students',
        gradeLevel: '',
        students: unassignedStudents
      };
    }

    return grouped;
  }, [classes, students, gradeFilter, classFilter, learningFlagsFilter, searchTerm, getGradeLevel]);

  // Handle field changes
  const handleFieldChange = (studentId, field, value) => {
    setEditedStudents(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  // Get current value for a student field (edited value or original)
  const getCurrentValue = (student, field) => {
    if (editedStudents[student.id] && editedStudents[student.id].hasOwnProperty(field)) {
      return editedStudents[student.id][field];
    }
    return student[field];
  };

  // Check if student has been edited
  const isStudentEdited = (studentId) => {
    return editedStudents[studentId] && Object.keys(editedStudents[studentId]).length > 0;
  };

  // Save all changes
  const handleSaveChanges = async () => {
    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const updates = Object.entries(editedStudents);
      let successCount = 0;
      let errorCount = 0;

      for (const [studentId, changes] of updates) {
        try {
          await updateStudent(studentId, changes);
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Failed to update student ${studentId}:`, err);
        }
      }

      if (successCount > 0) {
        setSuccessMessage(`Successfully updated ${successCount} student${successCount !== 1 ? 's' : ''}`);
        setEditedStudents({}); // Clear changes
        await loadData(); // Reload data
      }

      if (errorCount > 0) {
        setError(`Failed to update ${errorCount} student${errorCount !== 1 ? 's' : ''}. Please try again.`);
      }

    } catch (err) {
      setError("An error occurred while saving changes. Please try again.");
      console.error("Error saving changes:", err);
    } finally {
      setSaving(false);
    }
  };

  // Reset changes
  const handleReset = () => {
    setEditedStudents({});
    setError("");
    setSuccessMessage("");
  };

  // Close modal
  const handleClose = () => {
    handleReset();
    onHide();
  };

  const hasChanges = Object.keys(editedStudents).length > 0;
  const changesCount = Object.keys(editedStudents).length;
  const totalFilteredStudents = getFilteredStudentsByClass().length;
  const totalStudents = students.length;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Students</Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Loading students...</p>
          </div>
        ) : (
          <div>
            <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h5 className="mb-2">Edit Student Information</h5>
              <p className="mb-0 text-muted">
                Update student names and learning support flags. Changes are highlighted and will be saved when you click "Save Changes".
              </p>
            </div>

            {/* Filter Section */}
            <div className="mb-4 p-3" style={{ 
              backgroundColor: '#ffffff', 
              border: '1px solid #e2e8f0', 
              borderRadius: '8px' 
            }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 d-flex align-items-center">
                  <FaFilter className="me-2 text-muted" />
                  Filter Students
                </h6>
                <Badge bg="info" className="d-flex align-items-center">
                  <FaUsers className="me-1" size={10} />
                  {totalFilteredStudents} of {totalStudents} students
                </Badge>
              </div>
              
              <Row>
                <Col md={3}>
                  <Form.Label className="small text-muted">Grade Level</Form.Label>
                  <Form.Select
                    value={gradeFilter}
                    onChange={(e) => setGradeFilter(e.target.value)}
                    size="sm"
                  >
                    {gradeLevels.map(grade => (
                      <option key={grade.value} value={grade.value}>
                        {grade.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="small text-muted">Class</Form.Label>
                  <Form.Select
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                    size="sm"
                  >
                    {getAvailableClasses().map(cls => (
                      <option key={cls.value} value={cls.value}>
                        {cls.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="small text-muted">Learning Support</Form.Label>
                  <Form.Select
                    value={learningFlagsFilter}
                    onChange={(e) => setLearningFlagsFilter(e.target.value)}
                    size="sm"
                  >
                    {learningFlagsOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="small text-muted">Search</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Search students or classes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="sm"
                    />
                    <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                  </div>
                </Col>
              </Row>
              
              {/* Clear Filters Button */}
              {(gradeFilter !== "all" || classFilter !== "all" || learningFlagsFilter !== "all" || searchTerm.trim()) && (
                <Row className="mt-2">
                  <Col>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setGradeFilter("all");
                        setClassFilter("all");
                        setLearningFlagsFilter("all");
                        setSearchTerm("");
                      }}
                    >
                      Clear All Filters
                    </Button>
                  </Col>
                </Row>
              )}
            </div>

            <Accordion defaultActiveKey="0">
              {Object.keys(studentsByClass).length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '48px 24px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '2px dashed #d1d5db'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                  <p style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    No students found
                  </p>
                  <p style={{ color: '#6b7280' }}>
                    Try adjusting your filters or search terms to find students.
                  </p>
                </div>
              ) : (
                Object.entries(studentsByClass).map(([classId, classData], index) => (
                  <Accordion.Item eventKey={index.toString()} key={classId}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <span>
                          <strong>{classData.className}</strong>
                          {classData.gradeLevel && (
                            <span className="text-muted ms-2">({classData.gradeLevel})</span>
                          )}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg="primary">
                            {classData.students.length} student{classData.students.length !== 1 ? 's' : ''}
                          </Badge>
                          {classData.students.some(s => isStudentEdited(s.id)) && (
                            <Badge bg="warning" className="text-dark">
                              {classData.students.filter(s => isStudentEdited(s.id)).length} edited
                            </Badge>
                          )}
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {classData.students.length === 0 ? (
                        <p className="text-muted text-center py-3">No students in this class</p>
                      ) : (
                        <Table responsive size="sm">
                          <thead>
                            <tr>
                              <th style={{ minWidth: '200px' }}>Student Name</th>
                              <th className="text-center">EAL</th>
                              <th className="text-center">Speech & Language</th>
                              <th className="text-center">ASD</th>
                              <th className="text-center">GDD</th>
                              <th className="text-center">SEND</th>
                            </tr>
                          </thead>
                          <tbody>
                            {classData.students.map(student => (
                              <tr 
                                key={student.id}
                                style={{ 
                                  backgroundColor: isStudentEdited(student.id) ? '#fff3cd' : 'transparent' 
                                }}
                              >
                                <td>
                                  <Form.Control
                                    type="text"
                                    value={getCurrentValue(student, 'name')}
                                    onChange={(e) => handleFieldChange(student.id, 'name', e.target.value)}
                                    style={{ 
                                      border: getCurrentValue(student, 'name') !== student.name ? 
                                        '2px solid #ffc107' : '1px solid #ced4da'
                                    }}
                                  />
                                </td>
                                <td className="text-center">
                                  <Form.Check
                                    type="checkbox"
                                    checked={getCurrentValue(student, 'esl') || false}
                                    onChange={(e) => handleFieldChange(student.id, 'esl', e.target.checked)}
                                  />
                                </td>
                                <td className="text-center">
                                  <Form.Check
                                    type="checkbox"
                                    checked={getCurrentValue(student, 'sen') || false}
                                    onChange={(e) => handleFieldChange(student.id, 'sen', e.target.checked)}
                                  />
                                </td>
                                <td className="text-center">
                                  <Form.Check
                                    type="checkbox"
                                    checked={getCurrentValue(student, 'asd') || false}
                                    onChange={(e) => handleFieldChange(student.id, 'asd', e.target.checked)}
                                  />
                                </td>
                                <td className="text-center">
                                  <Form.Check
                                    type="checkbox"
                                    checked={getCurrentValue(student, 'gdd') || false}
                                    onChange={(e) => handleFieldChange(student.id, 'gdd', e.target.checked)}
                                  />
                                </td>
                                <td className="text-center">
                                  <Form.Check
                                    type="checkbox"
                                    checked={getCurrentValue(student, 'send') || false}
                                    onChange={(e) => handleFieldChange(student.id, 'send', e.target.checked)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                ))
              )}
            </Accordion>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            {hasChanges && (
              <div className="d-flex align-items-center">
                <span className="text-warning me-2">⚠️</span>
                <span className="text-muted">
                  {changesCount} student{changesCount !== 1 ? 's' : ''} modified
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ms-2"
                  onClick={handleReset}
                  disabled={saving}
                >
                  Reset Changes
                </Button>
              </div>
            )}
          </div>
          
          <div>
            <Button
              variant="secondary"
              className="me-2"
              onClick={handleClose}
              disabled={saving}
            >
              Close
            </Button>
            {hasChanges && (
              <Button
                variant="primary"
                onClick={handleSaveChanges}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving Changes...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default EditStudentsModal;